/**
 * L4: item detail (01-references §6 S6). It copies the 1Password iOS item screen: a header with
 * the item's icon, title, wallet and category; field rows whose tap opens Copy / Reveal / Show in
 * Large Type / Pin to Home; and an ellipsis menu with Favorite, Move, Duplicate, Archive, Delete.
 * This is a read-only copy of the vault (03 Option A), so the write actions are shown disabled.
 * Copy controls carry data-copy so the clear-on-next-tap guard does not fire on them.
 */
import { splitFieldRef, type AppActions, type AppState, type FieldRef, type VaultItem } from "../contract.js";
import { backButton, cell, emptyState, fakeChip, group, navBar, sheetFrame } from "../shell/chrome.js";
import { avatar, h, icon } from "../shell/dom.js";
import { detailModel, type DetailRow } from "../vm/detail.js";

const SVG = "http://www.w3.org/2000/svg";

function ring(secondsLeft: number, period: number): SVGSVGElement {
  const svg = document.createElementNS(SVG, "svg");
  svg.setAttribute("viewBox", "0 0 26 26");
  svg.setAttribute("class", "otp-ring");
  svg.setAttribute("aria-hidden", "true");
  const r = 10;
  const c = 2 * Math.PI * r;
  for (const [cls, dash] of [["track", `${c}`], ["progress", `${(c * secondsLeft) / period} ${c}`]]) {
    const el = document.createElementNS(SVG, "circle");
    el.setAttribute("cx", "13");
    el.setAttribute("cy", "13");
    el.setAttribute("r", String(r));
    el.setAttribute("class", cls);
    el.setAttribute("stroke-dasharray", dash);
    svg.appendChild(el);
  }
  return svg;
}

function copyButton(ref: FieldRef, label: string, actions: AppActions): HTMLElement {
  return h("button", { class: "icon-btn", "data-copy": "1", "aria-label": `Copy ${label}`, onclick: (e: Event) => { e.stopPropagation(); actions.copyField(ref); } }, icon("copy"));
}

function fieldRow(r: DetailRow, actions: AppActions): HTMLElement {
  const valueCls = ["field-value", r.masked ? "masked" : "", r.kind === "note" ? "note" : "", r.kind === "password" || r.field.sensitive ? "mono" : ""].join(" ").trim();
  const value =
    r.kind === "otp" && r.otp
      ? h("div", { class: `otp ${r.otp.expiring ? "expiring" : ""}`.trim() }, h("span", { class: "otp-code" }, r.display))
      : h("div", { class: valueCls }, r.display);
  const open = () => actions.openSheet({ kind: "field-menu", ...splitFieldRef(r.ref) });
  // A div with role=button, not a <button>: it holds the reveal and copy buttons, and buttons
  // must not nest.
  return h(
    "div",
    {
      class: "field",
      role: "button",
      tabindex: 0,
      "data-field": r.ref,
      onclick: open,
      onkeydown: (e: Event) => (e as KeyboardEvent).key === "Enter" && open(),
    },
    h(
      "div",
      { class: "field-main" },
      h("div", { class: "field-label" }, r.label, r.pinned ? " · pinned" : ""),
      value,
      r.revealed ? h("div", { class: "revealed-note" }, "Hides again in 30 seconds") : null,
    ),
    h(
      "div",
      { class: "field-actions" },
      r.otp ? ring(r.otp.secondsRemaining, r.otp.period) : null,
      r.canReveal
        ? h(
            "button",
            { class: "icon-btn", "aria-label": r.revealed ? `Hide ${r.label}` : `Reveal ${r.label}`, onclick: (e: Event) => { e.stopPropagation(); actions.toggleReveal(r.ref); } },
            icon(r.revealed ? "eye-off" : "eye"),
          )
        : null,
      copyButton(r.ref, r.label, actions),
    ),
  );
}

export function renderDetail(state: AppState, actions: AppActions): HTMLElement {
  const snap = state.snapshot!;
  const item = snap.items.find((i) => i.id === state.route.itemId);
  if (!item) {
    return h(
      "main",
      { class: "screen", "data-screen": "item" },
      navBar({ left: backButton("Back", actions), title: "Item" }),
      emptyState("items", "Item not found", "It is not in this copy of the vault."),
    );
  }
  const m = detailModel(item, state, snap.wallets);
  const header = navBar({
    left: backButton("Back", actions),
    title: "",
    right: h("button", { class: "navbar-btn", "aria-label": "More", onclick: () => actions.openSheet({ kind: "item-menu", itemId: item.id }) }, icon("more", "icon dots")),
  });
  return h(
    "main",
    { class: "screen", "data-screen": "item" },
    header,
    h(
      "div",
      { class: "detail-header" },
      avatar(item.title, "avatar lg"),
      h("div", { class: "detail-title" }, m.title, m.favorite ? h("span", { class: "star", "aria-label": "Favorite" }, " ", icon("star", "icon small fill")) : null),
      h("div", { class: "detail-meta" }, h("span", { class: "wallet-tag" }, m.walletName), m.categoryLabel, snap.fixture ? fakeChip() : null),
    ),
    h("section", { class: "group" }, h("div", { class: "group-body" }, ...m.rows.map((r) => fieldRow(r, actions)))),
    m.notes ? group("Notes", [fieldRow(m.notes, actions)], { small: true }) : null,
    m.tags.length ? group("Tags", [h("div", { class: "tags" }, ...m.tags.map(([k, v]) => h("span", { class: "tag" }, `${k}: ${v}`)))], { small: true }) : null,
    h("p", { class: "readonly-note" }, `${m.modifiedLabel}. Read-only copy.`),
  );
}

function findField(state: AppState, itemId: string, fieldId: string): { item: VaultItem; row: DetailRow } | null {
  const item = state.snapshot?.items.find((i) => i.id === itemId);
  if (!item) return null;
  const m = detailModel(item, state, state.snapshot!.wallets);
  const row = [...m.rows, ...(m.notes ? [m.notes] : [])].find((r) => r.field.id === fieldId);
  return row ? { item, row } : null;
}

export function renderFieldMenuSheet(state: AppState, actions: AppActions, itemId: string, fieldId: string): HTMLElement {
  const found = findField(state, itemId, fieldId);
  if (!found) return sheetFrame("Field", actions, emptyState("items", "Field not found", ""));
  const { row } = found;
  const then = (fn: () => void) => () => {
    fn();
    actions.closeSheet();
  };
  const copy = h("button", { class: "cell action", "data-copy": "1", onclick: then(() => actions.copyField(row.ref)) }, icon("copy"), h("span", { class: "cell-main" }, "Copy"));
  return sheetFrame(
    row.label,
    actions,
    group(null, [
      copy,
      row.canReveal ? cell({ label: row.revealed ? "Hide" : "Reveal", lead: icon(row.revealed ? "eye-off" : "eye"), cls: "action", onClick: then(() => actions.toggleReveal(row.ref)) }) : null,
      cell({ label: "Show in Large Type", lead: icon("search"), cls: "action", onClick: then(() => actions.showLargeType(row.ref)) }),
      cell({ label: row.pinned ? "Unpin from Home" : "Pin to Home", lead: icon("pin"), cls: "action", onClick: then(() => actions.togglePin(row.ref)) }),
    ]),
  );
}

export function renderItemMenuSheet(state: AppState, actions: AppActions, itemId: string): HTMLElement {
  const item = state.snapshot?.items.find((i) => i.id === itemId);
  if (!item) return sheetFrame("Item", actions);
  const fav = state.favoriteOverrides[item.id] ?? item.favorite;
  return sheetFrame(
    item.title,
    actions,
    group(null, [
      cell({ label: fav ? "Remove from Favorites" : "Add to Favorites", lead: icon("star"), cls: "action", onClick: () => { actions.toggleFavorite(item.id); actions.closeSheet(); } }),
    ]),
    group(
      null,
      [
        cell({ label: "Move", lead: icon("wallet"), disabled: true }),
        cell({ label: "Duplicate", lead: icon("copy"), disabled: true }),
        cell({ label: "Archive", lead: icon("archive"), disabled: true }),
        cell({ label: "Delete", lead: icon("trash"), disabled: true }),
      ],
      { foot: "This phone holds a read-only copy of your vault. Change items in Vowed on your Mac." },
    ),
  );
}
